import * as geom from "js/core/utilities/geom";
import { AnimationSequenceType, FormatType } from "common/constants.js";
import { TextGroup } from "js/elements/base/TextGroup";
import { SVGCircleElement, SVGPolylineElement } from "js/elements/base/SVGElement";
import { VerticalAlignType, HorizontalAlignType } from "common/constants";
import { layoutHelper } from "js/elements/layouts/LayoutHelper";
import CompareValuesItem from "./CompareValuesItem";
import CompareValuesItemLabel from "./CompareValuesItemLabel";

export default class CompareBubbleItem extends CompareValuesItem {
    get labelText() {
        return this.getRootElement().formatValue(this.currentValue);
    }

    _build() {
        this.shape = this.addElement("shape", () => SVGCircleElement);
        this.text = this.addElement("text", () => TextGroup, {
            autoHeight: true
        });

        this.removeElement(this.connector, false);
        if (this.parentElement.format != FormatType.NONE) {
            this.label = this.addElement("label", () => CompareValuesItemLabel, {
                model: { label: this.labelText },
                autoWidth: true,
                autoHeight: true,
                canEdit: false
            });
            this.connector = this.addElement("connector", () => SVGPolylineElement);
        }
    }

    calculateCircleSize(maxSize, value) {
        return maxSize * Math.sqrt(Math.max(value, 0.25) / Math.PI) / 5.641895835;
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        this.maxTextHeight = options.textHeight;

        let textProps = children.setProps(this.text, new geom.Size(size.width, this.maxTextHeight));

        const maxCircleSize = size.height - this.maxTextHeight;
        let circleSize = this.calculateCircleSize(Math.min(size.width, maxCircleSize), this.percentageValue * 100 * this.drawPercentage);
        let finalCircleSize = this.calculateCircleSize(Math.min(size.width, maxCircleSize), this.percentageValue * 100);

        let shapeProps = children.setProps(this.shape);
        shapeProps.layer = -1;

        if (this.isAnimating) {
            shapeProps.bounds = new geom.Rect(0, finalCircleSize - circleSize, size.width, circleSize);
        } else {
            shapeProps.bounds = new geom.Rect(0, 0, size.width, circleSize);
        }

        if (this.label) {
            if (this.isAnimating) {
                this.label.updateText(this.labelText);
            }

            let labelProps = children.setProps(this.label, new geom.Size(300, 300));

            let positionLabelInsideCircle = circleSize > labelProps.size.width && circleSize > labelProps.size.height;
            if (this.isAnimating) {
                positionLabelInsideCircle = this.finalLabelPositionIsInside;
            }

            if (positionLabelInsideCircle) {
                // position label within circle
                if (!this.isAnimating) {
                    this.label.isInside = true;
                }
                labelProps.bounds = new geom.Rect(shapeProps.bounds.centerH - circleSize / 2 + layoutHelper.getHorizontalAlignOffset(labelProps.size.width, circleSize, HorizontalAlignType.CENTER), shapeProps.bounds.top + layoutHelper.getVerticalAlignOffset(labelProps.size.height, shapeProps.bounds.height, VerticalAlignType.MIDDLE), labelProps.size);
            } else {
                // position label outside of circle with connector line
                if (!this.isAnimating) {
                    this.label.isInside = false;
                }
                labelProps.bounds = new geom.Rect(shapeProps.bounds.centerH + circleSize / 2, shapeProps.bounds.top - labelProps.size.height / 1.3333, labelProps.size);

                let connectorProps = children.setProps(this.connector);
                connectorProps.path = [[shapeProps.bounds.centerH + circleSize / 2 - circleSize * .333, shapeProps.bounds.top + circleSize * .333], [shapeProps.bounds.centerH + circleSize / 2 + 10, shapeProps.bounds.top - 10]];
            }
        }

        textProps.bounds = new geom.Rect(0, finalCircleSize, textProps.size);

        return { size: new geom.Size(size.width, Math.floor(finalCircleSize) + this.maxTextHeight) };
    }

    getBackgroundColor(forElement) {
        if (this.label && forElement === this.label && this.label.isInside) {
            return this.getShapeFillColor(this.shape);
        } else {
            return super.getBackgroundColor(forElement);
        }
    }

    get buildAnimations() {
        return [{
            target: this.DOMNode,
            type: AnimationSequenceType.FADE,
            duration: 400
        }, {
            type: AnimationSequenceType.ANIMATE_TO_VALUE,
            target: this,
            prepare: () => {
                this.animationState = { value: 0 };
                this.finalLabelPositionIsInside = this.label.isInside;
            },
            callback: value => {
                this.animationState = { value };
            },
            reset: () => {
                this.animationState = { value: 1 };
            },
            startValue: 0,
            endValue: 1,
        }, {
            type: AnimationSequenceType.FADE,
            target: this.text.DOMNode,
            delay: duration => duration - 400
        }];
    }
}
